import { useContext, useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import { useAppSelector } from 'hooks';
import type { HandGap } from 'utils/handPace';
import { createHandGapTracker, formatHandGap, handPaceDetail, handPaceLabel } from 'utils/handPace';

import FrameOrderRow from 'components/Debug/FrameOrderRow';

/** Gaps kept for the "prev" row: enough to see a trend, short enough to fit. */
const GAP_HISTORY = 4;

const TRIMMED_COLOR = '#4ade80';
const DEFAULT_COLOR = '#facc15';

/**
 * EN-10985: Performance HUD rows that let QA confirm the between-hand pace trim.
 *
 * - "hand pace": TRIMMED or default, as the server reports it for the table on
 *   screen, with the values behind it. Both sides cache the settings for a
 *   minute, so right after a change this can lead or lag the tables slightly.
 * - "last gap": measured on this client, from the frame that ends the betting
 *   (find/pay winners) to the next hand's deal frame (hand 4). A client-observed
 *   proxy for comparing paces before and after a change, not the ticket's
 *   engine-side "pot award to first action" measurement. On cash: about 16-18 s
 *   at the old pace, about 14-15 s at the deployed trim (chain off), about
 *   11-13 s with the hand chain on; more with tick slippage.
 *
 * Reads the table from the route (`/table/:gameID/...`) and that table's own
 * state, never the shared `currentTable`: every open table's frames update that
 * slice, and it is not cleared on leaving a table. Renders nothing away from a
 * table route (lobby, grid).
 */
const HandPaceRows = () => {
  const gameID = useMatch('/table/:gameID/:gameType')?.params.gameID ?? '';
  const hand = useAppSelector((state) => state.tables[gameID]?.game.hand);
  const gameNo = useAppSelector((state) => state.tables[gameID]?.game.gameNo);
  const languageKey = useAppSelector((state) => state.tables[gameID]?.game.languageKey);
  // Only cash frames carry handPace.
  const pace = useAppSelector((state) => state.tables[gameID]?.game.handPace);
  const { connectionStatus } = useContext(ConnectionManagerContext);

  const [tracker] = useState(createHandGapTracker);
  const gapsForRef = useRef(gameID);
  const [gaps, setGaps] = useState<HandGap[]>([]);

  // No frames are applied while the tab is hidden, so a gap open across it would
  // be timed from wherever the client came back.
  useEffect(() => {
    const dropGapIfHidden = () => {
      if (document.hidden) tracker.invalidate();
    };
    document.addEventListener('visibilitychange', dropGapIfHidden);
    return () => document.removeEventListener('visibilitychange', dropGapIfHidden);
  }, [tracker]);

  // Likewise while the update connection is down: frames sent meanwhile are lost,
  // and the snapshot after a reconnect is not the frame that came after the last one.
  useEffect(() => {
    if (connectionStatus !== 'connected') tracker.invalidate();
  }, [tracker, connectionStatus]);

  useEffect(() => {
    // Away from the table its frames are not observed here, so a gap open on
    // leaving would be closed on the return by a frame long after its own.
    if (!gameID) {
      tracker.invalidate();
      return;
    }
    if (hand === undefined) return;
    // A different table's gaps say nothing about this one.
    if (gapsForRef.current !== gameID) {
      gapsForRef.current = gameID;
      setGaps([]);
    }
    const gap = tracker.observe({ gameID, hand, gameNo, languageKey }, performance.now());
    if (gap) {
      setGaps((previous) => [gap, ...previous].slice(0, GAP_HISTORY));
    }
  }, [tracker, gameID, hand, gameNo, languageKey]);

  if (!gameID) return null;

  const label = handPaceLabel(pace);
  const [timers, chain] = pace ? handPaceDetail(pace) : ['', ''];
  const [last, ...previous] = gaps;

  return (
    <div
      data-testid='perf-hud-hand-pace'
      style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.12)' }}
      title={
        'Between-hand pace (EN-10985). "hand pace" is what the server runs for this ' +
        'cash table: TRIMMED when the handPace settings differ from the old pace. ' +
        'It can lag a settings change by about a minute. "last gap" is measured here, ' +
        'from the end of the betting to the next deal (hand 4), for before/after ' +
        'comparisons: on cash about 16-18s at the old pace, about 14-15s at the ' +
        'deployed trim (chain off), about 11-13s with the chain on.'
      }>
      <div>
        hand pace{' '}
        {label ? (
          <span style={{ color: pace?.isDefault ? DEFAULT_COLOR : TRIMMED_COLOR }}>{label}</span>
        ) : (
          <span style={{ opacity: 0.6 }}>n/a</span>
        )}
      </div>
      {pace ? (
        <>
          <div style={{ opacity: 0.6 }}>{timers}</div>
          <div style={{ opacity: 0.6 }}>{chain}</div>
        </>
      ) : null}
      <div>
        last gap{' '}
        {last ? formatHandGap(last) : <span style={{ opacity: 0.6 }}>waiting for a hand</span>}
      </div>
      {previous.length > 0 ? (
        <div style={{ opacity: 0.6 }}>prev {previous.map(formatHandGap).join('  ')}</div>
      ) : null}
      <FrameOrderRow gameID={gameID} />
    </div>
  );
};

export default HandPaceRows;
